import SettingsAppState from '../../apps/settings/state'
import getStorage from '../../storage/index'
import { PAGE_STATES } from './pageState'
import { setBackboneAppState } from './reduxLink'
import { NotFoundError } from './util/NotFoundError'
import { UnknownActivityError } from './util/UnknownActivityError'
import store from '@polleverywhere/redux'
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import { slice as activePresenterSlice } from './redux/activePresenter'
import { slice as responsePageSlice } from './redux/responsePage'
import { slice as appSlice } from '../../slices/app'
import listenToSelector from '../../util/listen_to_selector'
import BackboneSessionHelper from '../../util/backbone_session_helper'

store.addReducers({
  [activePresenterSlice.name]: activePresenterSlice.reducer,
  [responsePageSlice.name]: responsePageSlice.reducer
})

function resetLocalData () {
  this.set({
    settings: getStorage(this.get('user'), 'setting')
  })

  // Propagate theme change event
  this.get('settings').on('change:theme', (model, theme) => {
    this.trigger('change:theme', model, theme)
  })

  this.trigger('change:theme', this.get('settings'), this.get('settings').get('theme'))
}

/**
 * The pageState is loading async, so wait for that then update the state appropriately
 * dropping anything that comes in after another page change
 * @param {Promise<State>} pageStateResponse Promise resolving to new state properties
 * @param {number} pageLoadCount Which page load this was for
 */
function handleAsyncPageState (pageStateResponse, pageLoadCount) {
  pageStateResponse.then((stateProps) => {
    // Check it's the same page load before updating
    if (pageLoadCount === this.changePageCounter) {
      this.set(stateProps)
    } else {
      console.log('Scrapping async state update as page changed')
    }
  }).catch((e) => {
    // Check it's the same page load before updating
    if (pageLoadCount === this.changePageCounter) {
      if (e instanceof NotFoundError || e instanceof UnknownActivityError || e.status === 404) {
        store.dispatch(appSlice.pageUpdated('not_found'))
      } else if (e.status === 0) {
        this.trigger('notify:error', 'offline', e)
      } else {
        console.error('Error loading page async state', e)
        window.Rollbar?.error('Error loading page async state', e)
        this.trigger('notify:error', 'unknown', e)
      }
    } else {
      console.warn('Ignoring async state error as page changed', e)
    }
  })
}

/**
 * Tracks application state
 *
 * @event change:theme Theme change
 *  @property {String} name Theme name
 *
 * @event session:start User logged in and model hydrated
 *  @property {Model} user User model
 *
 * @event session:logout User logged out
 * @event session:anonymous No user on startup
 *
 */
export default class State extends Backbone.Model {
  get defaults () {
    return {
      settings: null,
      user: null,
      participant: null,
      pageState: null
    }
  }

  get role () {
    return store.getState()[appSlice.name].role
  }

  get user () {
    return this.get('user')
  }

  get participant () {
    return this.get('participant')
  }

  get legacySession () {
    return this.backboneSessionHelper.session
  }

  get pageState () {
    return this.get('pageState')
  }

  get hiddenTabBarItems () {
    if (this.get('user').id) {
      return 'log in'
    } else {
      return 'settings'
    }
  }

  initialize (attrs = {}, options = {}) {
    // Track states for apps
    this.appStates = {
      settings: new SettingsAppState()
    }

    // Track which page change this is so we can ignore some async actions
    this.changePageCounter = 0

    // Session is fully loaded with user
    this.on('session:start session:anonymous', () => {
      resetLocalData.call(this)

      // Session started. Set app to ready state
      if (store.getState()[profileSlice.name].participant?.must_consent) {
        // check whether we should show the terms page first
        store.dispatch(appSlice.pageUpdated('terms_agreement'))
      }
    })

    this.backboneSessionHelper = new BackboneSessionHelper(this).startSyncing()

    setBackboneAppState(this)

    listenToSelector((state) => state[appSlice.name].page, (page, prevPage) => {
      if (!/live_response|static_response|register/.test(page)) {
        store.dispatch(responsePageSlice.reset())
        store.dispatch(activePresenterSlice.reset())
      }

      if (page === 'response_history') {
        this.changePage('response_history')
      }
      if (page === 'registrations') {
        this.changePage('registrations')
      }
    })
  }

  changePage (
    page,
    pageStateOpts = {}
  ) {
    const pageLoadCount = ++this.changePageCounter
    const pageStateResponse = PAGE_STATES[page]?.call(this, pageStateOpts)
    let extraState = {}

    if (pageStateResponse?.then) {
      // A promise was returned for this page, so we'll wait for that
      // then update the state with that response later
      handleAsyncPageState.call(this, pageStateResponse, pageLoadCount)
    } else if (typeof pageStateResponse === 'object') {
      extraState = pageStateResponse
    }

    const newState = {
      pageState: undefined
    }
    this.set(_.defaults(extraState, newState))
  }

  joinPresenterSession (username) {
    store.dispatch(appSlice.pageUpdated('live_response'))
    this.changePage('live_response', { username })
  }

  showIndividualActivity (type, participantKey) {
    store.dispatch(appSlice.pageUpdated('static_response'))
    this.changePage('static_response', { type, participantKey })
  }

  enterRegisterFlow (username) {
    store.dispatch(responsePageSlice.reset())
    store.dispatch(appSlice.pageUpdated('register'))
    this.changePage('register', { username })
  }

  messageHistory () {
    return getStorage(this.get('user'), 'message')
  }

  logout () {
    store.dispatch(appSlice.pageUpdated('logout'))
  }

  inspect () {
    const result = super.toJSON()

    // Deal with state model attributes whose values are complex types
    result.participant = this.get('participant')?.toJSON()
    result.settings = this.get('settings')?.toJSON()
    result.user = this.get('user')?.toJSON().user

    return result
  }

  destroy () {
    this.backboneSessionHelper.stopSyncing()
  }
}
